import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner, useAuthStore, useCurrentUser } from '@dogdisk/shared'

/**
 * Hard auth guard — redirects unauthenticated users to /auth/login.
 * Use only on routes that strictly require login (wallet, profile, orders, ...).
 *
 * Public-by-default routes (home / files browse / share-pack) should NOT use
 * AppLayout; they handle the unauthenticated state inline (with a "登录"
 * prompt at the bottom rather than a forced redirect).
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { status } = useCurrentUser()
  const hydrate = useAuthStore((s) => s.hydrate)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    hydrate()
  }, [hydrate])

  // 未登录(含登出后)→ 命令式跳 /auth/login。
  // 不用渲染期 <Navigate>:原地 signOut 后 reactive <Navigate> 不真正改 URL(BUG-C11-1)。
  useEffect(() => {
    if (status === 'unauthenticated') {
      const next = encodeURIComponent(location.pathname)
      navigate(`/auth/login?next=${next}`, { replace: true })
    }
  }, [status, location.pathname, navigate])

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-white">
        <Spinner className="text-gray-400" />
      </div>
    )
  }

  return <>{children}</>
}

/**
 * Soft auth bootstrap — only hydrates the auth store, never redirects.
 * Pages wrap themselves in this when they want to render data even for
 * anonymous visitors (the "默认浏览数据无需登录" rule).
 */
export function PublicLayout({ children }: { children: ReactNode }) {
  const hydrate = useAuthStore((s) => s.hydrate)
  useEffect(() => {
    hydrate()
  }, [hydrate])
  return <>{children}</>
}
